import Phaser from 'phaser';
import {
  StateMachine,
  gridMove,
  frameMs,
  relativeDirection,
  oppositeDirection,
  weightedChoice,
  wait,
} from 'tower/util';
import BattleScene from 'tower/scenes/battle';
import Enemy, { EnemyOptions, EnemyState } from 'tower/scenes/battle/enemy';
import Projectile from './projectile';

const MOVE_SPEED = 35;

interface CentaurOptions extends EnemyOptions {
  blockedSpaces?: { x: number; y: number }[];
}

interface Variant {
  idle: number;
  hp: number;
  touchDamage: number;
}

const VARIANTS: Record<string, Variant> = {
  red: {
    idle: 320,
    hp: 5,
    touchDamage: 1,
  },
  blue: {
    idle: 352,
    hp: 7,
    touchDamage: 2,
  },
  dark: {
    idle: 384,
    hp: 4,
    touchDamage: 1,
  },
};

export default class Centaur extends Enemy {
  constructor(
    scene: BattleScene,
    x: number,
    y: number,
    public variantType = 'red',
    public centaurOptions: CentaurOptions = {},
  ) {
    const variant = VARIANTS[variantType];
    super(scene, x, y, 'enemies', variant.idle, 'centaur', {
      hp: variant.hp,
      touchDamage: variant.touchDamage,
      stunnable: true,
      stunDamage: false,
    });
    this.body.setSize(12, 12);
  }

  makeStateMachine(scene: BattleScene): StateMachine<EnemyState> {
    return new StateMachine('moving', {
      moving: new CentaurMovingState(scene, this),
      attack: new CentaurAttackState(scene, this),
    });
  }

  get variant() {
    return VARIANTS[this.variantType];
  }

  static create(scene: BattleScene) {
    for (const variant of Object.keys(VARIANTS)) {
      const idle = VARIANTS[variant].idle;
      scene.anims.create({
        key: `centaur[${variant}][down].move`,
        duration: frameMs(28),
        frames: scene.anims.generateFrameNumbers('enemies', {
          frames: [idle, idle + 1],
        }),
        repeat: -1,
      });
      scene.anims.create({
        key: `centaur[${variant}][up].move`,
        duration: frameMs(28),
        frames: scene.anims.generateFrameNumbers('enemies', {
          frames: [idle + 2, idle + 3],
        }),
        repeat: -1,
      });
      scene.anims.create({
        key: `centaur[${variant}][left].move`,
        duration: frameMs(28),
        frames: scene.anims.generateFrameNumbers('enemies', {
          frames: [idle + 4, idle + 5],
        }),
        repeat: -1,
      });
      scene.anims.create({
        key: `centaur[${variant}][right].move`,
        duration: frameMs(28),
        frames: scene.anims.generateFrameNumbers('enemies', {
          frames: [idle + 4, idle + 5],
        }),
        repeat: -1,
      });
    }
  }
}

class CentaurState extends EnemyState {
  declare enemy: Centaur;
}

class CentaurMovingState extends CentaurState {
  targetX!: number;
  targetY!: number;
  blockedFromPlayer = 0;
  reached = true;

  async handleEntered() {
    const { enemy, scene } = this;

    // If we did not reach our previous target, we got interrupted (e.g. hit) and should keep going.
    if (!this.reached) {
      return;
    }
    this.reached = false;

    const maze = scene.currentRoom.maze;
    const [sx, sy] = maze.worldXYToSpaceXY(enemy.x, enemy.y);

    // Filter out blocked spaces
    const openDirections = maze.getOpenDirectionsAtWorldXY(enemy.x, enemy.y).filter((d) => {
      const [nsx, nsy] = gridMove(sx, sy, d);
      if (enemy.centaurOptions.blockedSpaces?.some((blocked) => blocked.x === nsx && blocked.y === nsy)) {
        return false;
      }

      return true;
    });

    // Store a cooldown for if a wall separates us and the player
    const playerDirection = relativeDirection(enemy, scene.player);
    if (!openDirections.includes(playerDirection)) {
      this.blockedFromPlayer = 5;
    } else {
      this.blockedFromPlayer--;
    }

    const direction = weightedChoice(
      openDirections.map((d) => {
        // Prefer moving towards the player, if they are close and we aren't on cooldown
        if (
          d === playerDirection &&
          Phaser.Math.Distance.BetweenPoints(enemy, scene.player) < 24 * 4 &&
          this.blockedFromPlayer < 1
        ) {
          return [d, 2];
        }

        // Slightly prefer moving in the same direction
        if (d === enemy.direction) {
          return [d, 1.2];
        }

        // Prefer avoiding the direction we just came from
        if (d === oppositeDirection(enemy.direction)) {
          return [d, 0.3];
        }

        return [d, 1];
      }),
    );

    const [spaceX, spaceY] = maze.worldXYToSpaceXY(enemy.x, enemy.y);
    const [newSpaceX, newSpaceY] = gridMove(spaceX, spaceY, direction, 1);
    const [newWorldX, newWorldY] = maze.spaceXYToWorldXY(newSpaceX, newSpaceY);

    this.targetX = newWorldX;
    this.targetY = newWorldY;

    enemy.direction = direction;
    enemy.play(`centaur[${enemy.variantType}][${direction}].move`);
  }

  update() {
    const { enemy, scene } = this;
    scene.physics.moveTo(enemy, this.targetX, this.targetY, MOVE_SPEED);
    if (Phaser.Math.Distance.Between(enemy.x, enemy.y, this.targetX, this.targetY) <= 1) {
      enemy.body.reset(this.targetX, this.targetY);
      this.reached = true;
      return this.transition('attack');
    }

    // If blocked, give up
    if (!enemy.body.blocked.none) {
      enemy.body.stop();
      this.reached = true;
      return this.transition('attack');
    }
  }
}

class CentaurAttackState extends CentaurState {
  async handleEntered() {
    const { scene, enemy } = this;
    enemy.body.stop();

    let chance = 0.2;
    if (relativeDirection(enemy, scene.player) === enemy.direction) {
      chance = 0.35;
    }

    if (Math.random() > chance) {
      return this.transition('moving');
    }

    await wait(scene, 400);
    if (enemy.isDestroyed) {
      return;
    }

    new Projectile(scene, enemy.x, enemy.y, 'swordBeam', enemy.direction, { target: 'player' });
    await wait(scene, 250);
    if (enemy.isDestroyed) {
      return;
    }

    return this.transition('moving');
  }
}
